/**
 * §7.25 Minister Briefing Pack — Phase 9.4 of the decision-grade plan.
 *
 * Per CD R10's "the thesis: both surfaces live or die on how visibly
 * they handle uncertainty, contestation, and dissent" — every primitive
 * on this surface treats those as REQUIRED, not optional.
 *
 * 4-page modular structure (persona-modulated ceiling):
 *   P1 — Front page: Red-Box-style BLUF + Decision-required own row +
 *        RAG strip ≤6 indicators + Recommendation
 *   P2 — Situation + outlook: "what has happened / what we know / what
 *        we don't know" + three-scenario block (SPI-M-O best/central/RWCS)
 *   P3 — Decision options: A/B/C table with STRUCTURAL dissent rows
 *   P4 — Defence pack: "if asked X, say Y" + media Q&A + stakeholders
 *   Annex (optional, persona-gated): technical assumptions
 *
 * Persona variants reshape the pack — PM gets 1pp ceiling, Regional
 * Director gets 4pp + annex. Pack REFUSES to render past persona ceiling.
 *
 * Real pack-building flow ("pin to brief" → builder pre-populates BLUF
 * from pinned signals) lands Phase 9.7. v1 here renders the sample pack
 * so the structural primitives are visible + testable.
 */

import { useEffect, useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import { HonestyRail } from '../HonestyRail';
import {
  listBriefPins, subscribeToBriefPins, unpinForBrief, toggleSuppressPin,
  type PinnedItem,
} from '../pack/pin-to-brief';
import { generateBlufDraft } from '../pack/bluf-draft';
import { detectDarkSpots, dedupeMatchesByCountry } from '../pack/detect-dark-spots';
import { BlufDeltaStrip } from '../pack/BlufDeltaStrip';
import { ProvenanceChip } from '../pack/ProvenanceChip';
import { PhiaTag } from '../pack/PhiaTag';
import { ThreeScenarioBlock } from '../pack/ThreeScenarioBlock';
import { DecisionOptions } from '../pack/DecisionOptions';
import {
  PERSONA_SPECS, CADENCE_META,
  type Persona, type Cadence,
} from '../pack/types';
import {
  SAMPLE_PACK_HEADER, SAMPLE_BLUF_ASSERTIONS, SAMPLE_BLUF_PROSE,
  SAMPLE_DECISION_REQUIRED, SAMPLE_RAG, SAMPLE_RECOMMENDATION,
  SAMPLE_SCENARIOS, SAMPLE_OPTIONS,
} from '../pack/sample-pack';

type ActivePage = 'p1' | 'p2' | 'p3' | 'p4' | 'annex';

export function MinisterBriefingPack() {
  const [persona, setPersona] = useState<Persona>('health-minister');
  const [cadence, setCadence] = useState<Cadence>('morning');
  const [page, setPage] = useState<ActivePage>('p1');
  const [pinned, setPinned] = useState<readonly PinnedItem[]>(() => listBriefPins());
  useEffect(() => subscribeToBriefPins(setPinned), []);

  const spec = PERSONA_SPECS[persona];
  const isFlash = CADENCE_META[cadence].skipDefencePack;

  // Page-ceiling rail (CD R10 — pack refuses past ceiling)
  const availablePages: ActivePage[] = (() => {
    const out: ActivePage[] = ['p1'];
    if (spec.pageCeiling >= 2) out.push('p2');
    if (spec.pageCeiling >= 3) out.push('p3');
    if (spec.pageCeiling >= 4) out.push('p4');
    if (spec.annexAllowed) out.push('annex');
    // Urgent FLASH skips defence pack (P4) entirely
    if (isFlash) return out.filter(p => p !== 'p4');
    return out;
  })();
  // If current page no longer available after persona/cadence switch, drop to P1
  if (!availablePages.includes(page) && page !== 'p1') {
    setPage('p1');
  }

  const handlePersonaChange = (next: Persona) => {
    if (next === persona) return;
    auditCapture.emit({
      event: { type: 'persona.switch', surface: '§7.25', subjectIds: [],
        payload: { from: persona, to: next, packId: SAMPLE_PACK_HEADER.packId } },
    });
    setPersona(next);
  };

  const handleCadenceChange = (next: Cadence) => {
    if (next === cadence) return;
    auditCapture.emit({
      event: { type: 'scenario.change', surface: '§7.25', subjectIds: [],
        payload: { key: 'cadence', from: cadence, to: next } },
    });
    setCadence(next);
  };

  return (
    // `pacc-light` — render the leadership-facing OUTPUT as a light, print-
    // friendly island inside the dark command centre (briefings get printed/
    // projected; dark slides don't survive that). The class re-declares the
    // --bm-* neutrals + accent to the light brand and re-tunes threat/prov to
    // stay AA on white (see .pacc-light in styles.css). Honesty rails, EDU
    // chips, PHIA tags + provenance all re-skin light automatically.
    <div className="surface pacc-light">
      <div className="surface__header">
        <span className="surface__sec">§7.25</span>
        <h1 className="surface__title">Minister Briefing Pack</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Persona-tailored, PHIA-calibrated, dissent-structural. Built on real
          minister-briefing conventions (UK Red Box · US PDB · SPI-M-O RWCS · NSC NSSM).
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4,
          color: 'var(--bm-state-stale)',
          background: 'var(--bm-prov-stale-bg)',
          border: '1px solid var(--bm-state-stale)',
        }}>⚠ EDU · ILLUSTRATIVE SAMPLE PACK</span>
      </div>

      {/* Pack controls: persona selector + cadence selector + page nav */}
      <div style={{
        display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
        marginTop: 14, marginBottom: 14,
        padding: '10px 14px',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        <PersonaSelector value={persona} onChange={handlePersonaChange} />
        <CadenceSelector value={cadence} onChange={handleCadenceChange} />
        <span style={{ flex: 1 }} />
        <PageNav available={availablePages} current={page} onChange={setPage}
          ceiling={spec.pageCeiling} annexAllowed={spec.annexAllowed} />
      </div>

      {/* The pack itself — header band + active page */}
      <div style={{
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        overflow: 'hidden' as const,
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
      }}>
        <ClassificationBand header={{
          ...SAMPLE_PACK_HEADER,
          page: pageNumber(page),
          totalPages: availablePages.length,
        }} cadence={cadence} />
        <div style={{ padding: 16 }}>
          {page === 'p1' && <FrontPage pinned={pinned} />}
          {page === 'p2' && <SituationOutlook />}
          {page === 'p3' && <DecisionOptionsPage />}
          {page === 'p4' && !isFlash && <DefencePack pinned={pinned} />}
          {page === 'annex' && <Annex />}
        </div>
      </div>

      <PinnedItemsPanel pinned={pinned} />
      <PackHonestyRail persona={persona} cadence={cadence} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 1 — Front page (the only page guaranteed to be read)
// ---------------------------------------------------------------------------

function FrontPage({ pinned }: { pinned: readonly PinnedItem[] }) {
  // Parse {likely} / {growth} placeholders into PhiaTag components.
  const blufNodes: React.ReactNode[] = [];
  const remaining = SAMPLE_BLUF_PROSE.split(/(\{likely\}|\{growth\})/g);
  for (let i = 0; i < remaining.length; i++) {
    const part = remaining[i];
    if (part === '{likely}') {
      blufNodes.push(<PhiaTag key={i} assertion={SAMPLE_BLUF_ASSERTIONS.likely} />);
    } else if (part === '{growth}') {
      blufNodes.push(<PhiaTag key={i} assertion={SAMPLE_BLUF_ASSERTIONS.growth} />);
    } else if (part) {
      blufNodes.push(<span key={i}>{part}</span>);
    }
  }

  // Phase 9.7 v2 — auto-draft BLUF from pinned items (if any). Renders
  // ABOVE the sample BLUF as a "suggested draft" — operator-as-editor.
  const draft = useMemo(() => generateBlufDraft(pinned), [pinned]);
  const draftNodes: React.ReactNode[] = [];
  if (draft.clauses.length > 0) {
    const parts = draft.combinedProse.split(/(\{phia\d+\})/g);
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const match = part?.match(/^\{phia(\d+)\}$/);
      if (match) {
        const idx = parseInt(match[1]!, 10);
        const a = draft.combinedPhia[idx];
        if (a) draftNodes.push(<PhiaTag key={i} assertion={a} />);
      } else if (part) {
        draftNodes.push(<span key={i}>{part}</span>);
      }
    }
  }

  return (
    <>
      {/* Phase 9.7 Q9 — version-diff strip (P0.5) above the BLUF.
          Sample-driven; real pack-history persistence is Phase 11+. */}
      <BlufDeltaStrip />

      {/* Phase 9.7 v2 — auto-draft surfaces when ≥1 pinned item yields a draftable clause */}
      {draft.clauses.length > 0 && (
        <div style={{
          marginBottom: 14, padding: '12px 14px',
          background: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
          border: '1px solid var(--bm-accent, #6ea3ff)',
          borderRadius: 'var(--bm-radius-md)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 8, marginBottom: 6,
          }}>
            <SectionLabel color="var(--bm-accent, #6ea3ff)">
              SUGGESTED BLUF · auto-drafted from {draft.clauses.length} pinned {draft.clauses.length === 1 ? 'item' : 'items'}
            </SectionLabel>
            <span style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
              color: 'var(--bm-text-subtle)',
            }}>operator-editable · polish before publish</span>
          </div>
          <p style={{
            margin: '5px 0 0', fontSize: 14, lineHeight: 1.55, color: 'var(--bm-text)',
          }}>{draftNodes}</p>
          <div style={{
            marginTop: 8, fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
            color: 'var(--bm-text-subtle)',
          }}>
            ▦ drawn from {draft.drawnFrom} · heuristic draft (Phase 11+ swaps for grounded-LLM with per-sentence citations)
          </div>
        </div>
      )}

      {/* BLUF — Red Box style: BLUF goes ABOVE the RAG strip per CD R10 Q1.
          Sample BLUF rendered below the auto-draft so operator can compare
          before replacing the sample with the auto-draft text. */}
      <div style={{ marginBottom: 14 }}>
        <SectionLabel color="var(--bm-accent, #6ea3ff)">
          {draft.clauses.length > 0 ? 'SAMPLE BLUF (placeholder — replace with draft above)' : 'BLUF'}
        </SectionLabel>
        <p style={{
          margin: '5px 0 0', fontSize: 15, lineHeight: 1.55, color: 'var(--bm-text)',
        }}>
          {blufNodes}
        </p>
      </div>

      {/* Decision-required — own row per CD R10 Q1 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: 'var(--bm-threat-1-bg, rgba(248,113,113,0.14))',
        border: '1px solid var(--bm-threat-1, #f87171)',
        borderRadius: 7,
        padding: '9px 12px', marginBottom: 14,
      }}>
        <SectionLabel color="var(--bm-threat-1, #f87171)">DECISION REQUIRED</SectionLabel>
        <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{SAMPLE_DECISION_REQUIRED.text}</span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700,
          color: 'var(--bm-threat-1, #f87171)',
        }}>by {SAMPLE_DECISION_REQUIRED.byTime}</span>
      </div>

      {/* RAG strip — ≤6 indicators per CD R10 */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
        marginBottom: 14,
      }}>
        {SAMPLE_RAG.map(r => (
          <div key={r.name} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--bm-bg)',
            border: '1px solid var(--bm-border)', borderRadius: 6,
            padding: '7px 10px',
          }}>
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flex: 'none',
              background: r.status === 'red' ? 'var(--bm-threat-1, #f87171)'
                : r.status === 'amber' ? 'var(--bm-threat-3, #fbbf24)'
                : 'var(--bm-prov-confirmed, var(--bm-state-live))',
            }} />
            <span style={{ fontSize: 11.5, color: 'var(--bm-text)', flex: 1 }}>{r.name}</span>
            <span style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 10,
              color: 'var(--bm-text-subtle)',
            }}>{r.value}</span>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      <div style={{
        fontSize: 13, color: 'var(--bm-text)',
        background: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
        borderRadius: 7, padding: '9px 12px',
      }}>
        <SectionLabel color="var(--bm-accent, #6ea3ff)">RECOMMENDED</SectionLabel>
        {' '}{SAMPLE_RECOMMENDATION}{' '}
        <span style={{ color: 'var(--bm-text-subtle)' }}>Rationale + 2 alternatives → P3.</span>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page 2 — Situation + outlook (WHO DG cadence: what has happened / what
// we know / what we don't know + three-scenario block)
// ---------------------------------------------------------------------------

function SituationOutlook() {
  return (
    <>
      <SectionLabel color="var(--bm-text-muted)">SITUATION + OUTLOOK</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 8, marginBottom: 18 }}>
        <SituationBlock title="What has happened" body="Cluster of confirmed filovirus cases reported in North Kivu province (DR Congo) over the past 72 hours; 14 confirmed, 4 deaths. Genomic sequencing in progress." />
        <SituationBlock title="What we know" body="Cases concentrated in three border villages; reproductive number from early modelling 2.1–3.4. Vaccination supply: 12,000 doses on-hand in Kinshasa; cold-chain to North Kivu intact." />
        <SituationBlock title="What we don't know" body="Whether genomic strain matches stockpiled vaccine. Cross-border transmission status (Uganda has informal-trade routes, no formal screening). Whether Mai-Mai armed-group activity will impair response (DRC 2018-20 pattern)." />
      </div>

      <SectionLabel color="var(--bm-text-muted)">FORECAST — three scenarios (SPI-M-O convention)</SectionLabel>
      <div style={{ marginTop: 8 }}>
        <ThreeScenarioBlock scenarios={SAMPLE_SCENARIOS} />
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-confirmed, var(--bm-state-live))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Three-scenario block, not a single forecast.</b>{' '}
        Best / Central / RWCS bands carry independent confidence; the central case is the
        planning case (operator's working assumption). Visually distinct from the §7.4
        counterfactual divergence chart — those are policy divergences; these are forecast
        scenarios with bounded uncertainty.
      </div>
    </>
  );
}

function SituationBlock({ title, body }: { title: string; body: string }) {
  return (
    <div style={{
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
      padding: 12,
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: '.6px', textTransform: 'uppercase',
        color: 'var(--bm-text-subtle)', marginBottom: 6,
      }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--bm-text)', lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page 3 — Decision options (with structural dissent rows)
// ---------------------------------------------------------------------------

function DecisionOptionsPage() {
  return (
    <>
      <SectionLabel color="var(--bm-text-muted)">DECISION OPTIONS</SectionLabel>
      <div style={{ marginTop: 8 }}>
        <DecisionOptions options={SAMPLE_OPTIONS} enforceDissentSurfacing />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page 4 — Defence pack ("if asked X, say Y" + media Q&A + stakeholders)
// ---------------------------------------------------------------------------

function DefencePack({ pinned }: { pinned: readonly PinnedItem[] }) {
  // CD R11 Finding #4 — Dark-spot auto-callout. When ANY pinned item touches
  // one of the 9 non-disseminator countries, the defence pack MUST surface
  // "this region is a surveillance dark spot — absence of signal is not
  // absence of disease." This is the single most important honesty claim in
  // the product; it must fire mechanically, not be an operator afterthought.
  // CI gate at apps/pacc-prototype/tests/dark-spot-callout.test.ts.
  const darkSpots = useMemo(
    () => dedupeMatchesByCountry(detectDarkSpots(pinned)),
    [pinned],
  );

  // Per CD R10 Q6: "LLM-draft + per-line confidence indicator." v1 here
  // renders sample defence-pack lines with confidence chips; real LLM-draft
  // ingestion from past-72h media tracker lands Phase 9.7+.
  const lines: Array<{ q: string; a: string; confidence: 'Low' | 'Moderate' | 'High' }> = [
    { q: 'If asked whether the outbreak is "contained":', a: 'Say: We have early case detection plus active contact tracing. Containment will depend on cross-border cooperation and the supply package the Minister is considering.', confidence: 'High' },
    { q: 'If asked about the cost of inaction:', a: 'Say: Modelling suggests the reasonable-worst-case is 18,000 cases by week 9. The CI is wide; we will share updated estimates with the Committee tomorrow.', confidence: 'Moderate' },
    { q: 'If asked why we have not closed borders:', a: 'Say: Travel measures of that scope require legal authority we do not currently hold; the Minister has been advised on the path to obtaining it. Targeted measures are operationally faster and historically more effective.', confidence: 'Moderate' },
  ];
  const stakeholders: string[] = [
    'WHO AFRO Regional Director',
    'Uganda Health Minister',
    'PMO Health Lead',
    'Cabinet Secretary',
    'Opposition Health Critic (advance notice)',
  ];
  return (
    <>
      <SectionLabel color="var(--bm-text-muted)">DEFENCE PACK</SectionLabel>

      {/* CD R11 #4 — DARK SPOT callout panel (auto-fires per matched country).
          Rendered ABOVE the canned defence lines so the operator can't miss it
          while skimming. Tested in apps/pacc-prototype/tests/dark-spot-callout.test.ts. */}
      {darkSpots.length > 0 && (
        <div data-testid="dark-spot-callouts" style={{ marginTop: 10 }}>
          {darkSpots.map(m => (
            <HonestyRail key={m.country.iso3} variant="limitation" label="DARK SPOT">
              <b>{m.country.name}</b> is a surveillance dark spot — <i>absence of
              signal is not absence of disease.</i> {m.country.rationale}
              {m.country.indirectInferenceSources && m.country.indirectInferenceSources.length > 0 && (
                <>
                  {' '}<span style={{ color: 'var(--bm-text-subtle)' }}>
                    Indirect inference channels: {m.country.indirectInferenceSources.join('; ')}.
                  </span>
                </>
              )}
              {m.country.operatorAction && (
                <>
                  {' '}<span style={{ color: 'var(--bm-text-subtle)' }}>
                    Operator action: {m.country.operatorAction}
                  </span>
                </>
              )}
            </HonestyRail>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8, marginBottom: 14 }}>
        {lines.map((l, i) => (
          <div key={i} style={{
            padding: '9px 12px', marginBottom: 8,
            background: 'var(--bm-bg-subtle)',
            border: '1px solid var(--bm-border)',
            borderRadius: 7,
          }}>
            <div style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
              color: 'var(--bm-text-muted)', marginBottom: 4,
            }}>{l.q}</div>
            <div style={{ fontSize: 12.5, color: 'var(--bm-text)', lineHeight: 1.5 }}>{l.a}</div>
            <div style={{ marginTop: 6 }}>
              <ConfidenceChip confidence={l.confidence} />
              <span style={{
                marginLeft: 8, fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
                color: 'var(--bm-text-subtle)',
              }}>LLM-draft · operator-editable</span>
            </div>
          </div>
        ))}
      </div>
      <SectionLabel color="var(--bm-text-muted)">STAKEHOLDERS TO CALL (before public announcement)</SectionLabel>
      <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12.5, color: 'var(--bm-text)' }}>
        {stakeholders.map(s => <li key={s} style={{ marginBottom: 3 }}>{s}</li>)}
      </ul>
    </>
  );
}

function ConfidenceChip({ confidence }: { confidence: 'Low' | 'Moderate' | 'High' }) {
  const color = confidence === 'High' ? 'var(--bm-prov-confirmed, var(--bm-state-live))'
    : confidence === 'Moderate' ? 'var(--bm-threat-3, #fbbf24)'
    : 'var(--bm-text-subtle)';
  return (
    <span style={{
      padding: '1px 7px', fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
      color, border: `1px solid ${color}`, borderRadius: 4,
    }}>draft conf · {confidence}</span>
  );
}

// ---------------------------------------------------------------------------
// Annex — technical assumptions + model versions + freshness + dissent log
// ---------------------------------------------------------------------------

function Annex() {
  return (
    <>
      <SectionLabel color="var(--bm-text-muted)">ANNEX — assumptions, model versions, freshness, citations</SectionLabel>
      <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
        <p><b style={{ color: 'var(--bm-text)' }}>Model versions.</b> Engine v0.4 (SEIRD-vector). Intervention basket v0.3. NPI catalog 2026-05-18.</p>
        <p><b style={{ color: 'var(--bm-text)' }}>Data freshness.</b> WHO DON: 2h ago. ProMED: 4h. HealthMap: real-time. WPRO bulletin: 38h.</p>
        <p><b style={{ color: 'var(--bm-text)' }}>Forecast assumptions.</b> R0 prior 2.1–3.4 (Lassa Nigeria 2018-20 historical band; PMID 30531992). Generation interval 12.7d (range 9-18d). IFR 0.42 (CI 0.31–0.55).</p>
        <p><b style={{ color: 'var(--bm-text)' }}>Dissent log.</b> Modelling cell 2 dissenters (40% lower averted-cases CI). Senior legal adviser (authority contestable). See P3.</p>
        <p style={{ marginTop: 14, fontSize: 11, color: 'var(--bm-text-subtle)' }}>
          Pack assembly: v1 (Phase 9.4) renders sample. Real pack-build from pinned signals
          + scenarios is Phase 9.7. Full citation list will round-trip via
          @biomax/plugin-literature-fetch.
        </p>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Chrome — classification band + persona/cadence/page selectors
// ---------------------------------------------------------------------------

function ClassificationBand({ header, cadence }: { header: PackHeaderRendered; cadence: Cadence }) {
  const cadenceMeta = CADENCE_META[cadence];
  const dateTime = (() => {
    const d = new Date(header.asOf);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}${mm}Z`;
  })();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      padding: '8px 14px',
      background: 'var(--bm-bg-subtle)',
      borderBottom: '1px solid var(--bm-border)',
      fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
      color: 'var(--bm-text-subtle)',
    }}>
      <span style={{
        fontWeight: 700,
        color: header.classification === 'OFFICIAL-SENSITIVE' || header.classification === 'SECRET' || header.classification === 'TOP-SECRET'
          ? 'var(--bm-threat-3, #fbbf24)' : 'var(--bm-text-muted)',
        letterSpacing: '.5px',
      }}>{header.classification}</span>
      <span>{dateTime}</span>
      <span>Dist: {header.distribution}</span>
      <span>Pack {header.packId}</span>
      <span style={{ marginLeft: 'auto' }}>
        ⧗ {header.readTimeMin}min read · {cadenceMeta.label} · P{header.page} of {header.totalPages}
      </span>
    </div>
  );
}
interface PackHeaderRendered {
  classification: string;
  asOf: string;
  distribution: string;
  packId: string;
  readTimeMin: number;
  page: number;
  totalPages: number;
}

function PersonaSelector({ value, onChange }: { value: Persona; onChange: (p: Persona) => void }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>
      <span style={{ color: 'var(--bm-text-subtle)' }}>Persona</span>
      <select value={value} onChange={e => onChange(e.target.value as Persona)}
        style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5,
          padding: '3px 6px',
          background: 'var(--bm-bg)', color: 'var(--bm-text)',
          border: '1px solid var(--bm-border)', borderRadius: 4,
        }}>
        {Object.values(PERSONA_SPECS).map(p => (
          <option key={p.id} value={p.id}>{p.label} ({p.pageCeiling}pp{p.annexAllowed ? '+annex' : ''})</option>
        ))}
      </select>
    </label>
  );
}

function CadenceSelector({ value, onChange }: { value: Cadence; onChange: (c: Cadence) => void }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--bm-border)', borderRadius: 5, overflow: 'hidden' as const }}>
      {(['morning', 'end-of-day', 'urgent-flash'] as readonly Cadence[]).map(c => (
        <button key={c} type="button" onClick={() => onChange(c)}
          title={CADENCE_META[c].description}
          style={{
            padding: '4px 10px', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
            background: value === c
              ? (c === 'urgent-flash' ? 'var(--bm-threat-1, #f87171)' : 'var(--bm-accent, #6ea3ff)')
              : 'transparent',
            color: value === c ? '#fff' : 'var(--bm-text-muted)',
          }}>{CADENCE_META[c].label}</button>
      ))}
    </div>
  );
}

function PageNav({ available, current, onChange, ceiling, annexAllowed }: {
  available: readonly ActivePage[];
  current: ActivePage;
  onChange: (p: ActivePage) => void;
  ceiling: 1 | 2 | 3 | 4;
  annexAllowed: boolean;
}) {
  return (
    <div style={{ display: 'inline-flex', gap: 4 }}>
      {(['p1', 'p2', 'p3', 'p4', 'annex'] as readonly ActivePage[]).map(p => {
        const blocked = !available.includes(p);
        const label = p === 'p1' ? 'Front' : p === 'p2' ? 'Situation' : p === 'p3' ? 'Options' : p === 'p4' ? 'Defence' : 'Annex';
        return (
          <button key={p} type="button"
            onClick={() => !blocked && onChange(p)}
            disabled={blocked}
            title={blocked ? `Persona ceiling: ${ceiling}pp${annexAllowed ? '+annex' : ''} — page blocked` : ''}
            style={{
              padding: '4px 10px',
              cursor: blocked ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
              background: current === p ? 'var(--bm-bg-strong, var(--bm-bg))' : 'transparent',
              color: blocked ? 'var(--bm-text-subtle)' : (current === p ? 'var(--bm-text)' : 'var(--bm-text-muted)'),
              opacity: blocked ? 0.4 : 1,
              textDecoration: blocked ? 'line-through' as const : 'none' as const,
              border: '1px solid var(--bm-border)', borderRadius: 4,
            }}>{label}</button>
        );
      })}
    </div>
  );
}

function SectionLabel({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
      letterSpacing: '.8px', color,
    }}>{children}</span>
  );
}

function pageNumber(p: ActivePage): number {
  return p === 'p1' ? 1 : p === 'p2' ? 2 : p === 'p3' ? 3 : p === 'p4' ? 4 : 5;
}

// ---------------------------------------------------------------------------
// Pack-level honesty rail
// ---------------------------------------------------------------------------

/** Phase 9.7 — pinned items panel. Shows what the operator has pinned
 *  across the session for this Pack. Real Builder pre-population from
 *  these pins (BLUF + indicator strip auto-draft) is the next iteration;
 *  v1 lists them so the operator can see the in-flight collection. */
function PinnedItemsPanel({ pinned }: { pinned: readonly PinnedItem[] }) {
  if (pinned.length === 0) return null;
  const active = pinned.filter(p => !p.suppressed).length;
  const suppressed = pinned.length - active;
  return (
    <div style={{
      marginTop: 14, padding: '10px 14px',
      background: 'var(--bm-accent-subtle, rgba(110,163,255,0.14))',
      border: '1px solid var(--bm-accent, #6ea3ff)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '.5px', textTransform: 'uppercase' as const,
        color: 'var(--bm-accent, #6ea3ff)', marginBottom: 6,
      }}>📌 Pinned for this brief · {active} active{suppressed > 0 ? ` · ${suppressed} suppressed` : ''}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {pinned.map(p => {
          const origin = p.suppressed ? 'operator-suppressed' : 'operator-added';
          return (
            <div
              key={p.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const,
                padding: '4px 9px',
                fontFamily: 'var(--bm-font-mono)', fontSize: 11,
                color: p.suppressed ? 'var(--bm-text-subtle)' : 'var(--bm-text)',
                background: 'var(--bm-bg)',
                border: '1px solid var(--bm-border)',
                borderRadius: 4,
                opacity: p.suppressed ? 0.6 : 1,
              }}
            >
              <ProvenanceChip origin={origin} small />
              <span style={{ color: 'var(--bm-text-subtle)' }}>{p.surface}</span>
              <span style={{
                textDecoration: p.suppressed ? 'line-through' as const : 'none' as const,
                flex: 1, minWidth: 0,
              }}>{p.label}</span>
              <button
                type="button"
                onClick={() => toggleSuppressPin(p.id)}
                title={p.suppressed ? 'Include in this brief' : 'Suppress from this brief (kept in queue)'}
                style={{
                  padding: '1px 7px',
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10,
                  color: 'var(--bm-text-muted)', background: 'transparent',
                  border: '1px solid var(--bm-border)', borderRadius: 3, cursor: 'pointer',
                }}
              >{p.suppressed ? 'include' : 'suppress'}</button>
              <button
                type="button"
                onClick={() => unpinForBrief(p.id)}
                title="Unpin (remove from queue entirely)"
                style={{
                  padding: '1px 7px',
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10,
                  color: 'var(--bm-text-muted)', background: 'transparent',
                  border: '1px solid var(--bm-border)', borderRadius: 3, cursor: 'pointer',
                }}
              >unpin ✕</button>
            </div>
          );
        })}
      </div>
      <div style={{
        marginTop: 8, fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
        color: 'var(--bm-text-subtle)',
      }}>
        Origin chips: 📌 OP = operator-added · ◑ AUTO = auto-attached (Phase 9.5 pipeline) · ✕ SUP = operator-suppressed
      </div>
    </div>
  );
}

function PackHonestyRail({ persona, cadence }: { persona: Persona; cadence: Cadence }) {
  const spec = PERSONA_SPECS[persona];
  return (
    <div style={{
      marginTop: 14, padding: '10px 14px',
      fontSize: 11.5, color: 'var(--bm-text-subtle)', lineHeight: 1.5,
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <b style={{ color: 'var(--bm-text)' }}>Pack persona:</b> {spec.label} ·
      ceiling {spec.pageCeiling}pp{spec.annexAllowed ? ' + annex' : ''} ·
      <b style={{ color: 'var(--bm-text)' }}> cadence:</b> {CADENCE_META[cadence].label} ·
      every assertion carries PHIA + analytical-confidence; every decision option
      carries structural dissent (anti-SAGE-2020). Real pack-build from pinned
      signals + scenarios lands Phase 9.7.
    </div>
  );
}
